import { useRef, useCallback, useEffect, useState, isValidElement, createElement } from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBodyScrollLock, useEscapeKey, useFocusTrap, useScrollShadow } from '../hooks/useModalBehavior';

const SIZES = {
  sm: '28rem',
  md: '32rem',
  lg: '50rem',
  xl: '56rem'
};

/**
 * StepIndicator — Horizontal stepper for multi-section modals.
 */
function StepIndicator({ steps, isRTL }) {
  if (!steps || steps.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0,
      padding: '0.75rem 1rem 0.25rem',
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      {steps.map((step, i) => {
        const isCompleted = step.completed;
        const isActive = step.active;
        const isFuture = !isCompleted && !isActive;

        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '2rem' }}>
              <div style={{
                width: '1.625rem',
                height: '1.625rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6875rem',
                fontWeight: '600',
                flexShrink: 0,
                transition: 'all 0.3s ease',
                ...(isCompleted ? {
                  backgroundColor: 'var(--color-primary, #355760)',
                  color: 'var(--color-on-primary, #fff)',
                } : isActive ? {
                  backgroundColor: 'transparent',
                  border: '2px solid var(--color-primary, #355760)',
                  color: 'var(--color-primary, #355760)',
                } : {
                  backgroundColor: 'var(--color-surface-alt, #F1F4F8)',
                  color: 'var(--color-text-tertiary, #697386)',
                }),
              }}>
                {isCompleted ? <Check size={12} strokeWidth={3} /> : i + 1}
              </div>
              <span style={{
                fontSize: '0.625rem',
                fontWeight: isActive ? '600' : '400',
                color: isFuture ? 'var(--color-text-tertiary, #697386)' : 'var(--color-text-secondary, #3C4257)',
                marginTop: '0.25rem',
                whiteSpace: 'nowrap',
                maxWidth: '4.5rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center',
                transition: 'color 0.2s ease',
              }}>
                {step.label}
              </span>
            </div>

            {i < steps.length - 1 && (
              <div style={{
                flex: 1,
                height: '2px',
                marginBottom: '1.25rem',
                marginInline: '0.375rem',
                borderRadius: '1px',
                backgroundColor: isCompleted ? 'var(--color-primary, #355760)' : 'var(--color-border, #E2E6EE)',
                transition: 'background-color 0.3s ease',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * EnterpriseModal - Shared modal wrapper with enterprise UX patterns.
 *
 * Features:
 * - Portal rendering (consistent z-index)
 * - Sticky header with scroll shadow
 * - Scrollable body
 * - Fixed footer with shadow
 * - Click-outside-to-close
 * - Escape key handler
 * - Body scroll lock
 * - Focus trap
 * - RTL support
 * - Mobile bottom sheet mode
 * - Accent strip (branded top bar)
 * - Backdrop blur
 * - Spring-based animations
 * - Step indicator for multi-section forms
 * - Progress bar
 * - Mobile drag handle
 */
export default function EnterpriseModal({
  isOpen,
  onClose,
  size = 'md',
  title,
  subtitle,
  titleIcon,
  headerContent,
  footer,
  children,
  closeOnBackdrop = true,
  closeOnEscape = true,
  isSubmitting = false,
  // Phase 1.7b: default to bottom-sheet on mobile. Pass `false` to opt out
  // (e.g. tiny destructive confirms where a centered dialog reads better).
  mobileBottomSheet = true,
  // Phones only: the sheet takes the full height (a long pick list) instead
  // of growing with its content.
  mobileFullHeight = false,
  hideCloseButton = false,
  bodyPadding = '1.5rem',
  steps,
  progress,
  dataTestId,
}) {
  const { i18n, t } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  const modalRef = useRef(null);
  const bodyRef = useRef(null);

  const handleClose = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);

  useBodyScrollLock(isOpen);
  useEscapeKey(handleClose, isOpen, { disabled: !closeOnEscape || isSubmitting });
  useFocusTrap(modalRef, isOpen);
  const { headerShadow, footerShadow } = useScrollShadow(bodyRef);

  // Track the on-screen keyboard's height via the VisualViewport API so the
  // mobile bottom-sheet stays above it instead of getting buried (most
  // visible on iOS Safari, where opening the keyboard does not reflow the
  // layout viewport that `position:fixed` is anchored to). For the centered
  // desktop modal this stays at 0 and the effect is a no-op.
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  useEffect(() => {
    if (!isOpen) { setKeyboardOffset(0); return undefined; }
    if (typeof window === 'undefined' || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const update = () => {
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      setKeyboardOffset(offset > 80 ? offset : 0);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // `titleIcon` is meant to be an element (`<CreditCard size={20} />`), but a
  // bare component type (`CreditCard`) is an easy slip — and under React 19
  // rendering one as a child throws, which takes the whole page down to the
  // error boundary the moment the modal opens. Accept either form.
  const titleIconNode = isValidElement(titleIcon)
    ? titleIcon
    : (typeof titleIcon === 'function'
        || (titleIcon && typeof titleIcon === 'object' && titleIcon.$$typeof))
      ? createElement(titleIcon, { size: 20 })
      : titleIcon;

  const maxWidth = SIZES[size] || SIZES.md;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const useBottomSheet = mobileBottomSheet && isMobile;

  const hasHeader = headerContent || title;
  const hasFooter = !!footer;
  const hasProgress = typeof progress === 'number';

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1040,
        display: 'flex',
        alignItems: useBottomSheet ? 'flex-end' : 'center',
        justifyContent: 'center',
        // Pad the bottom by the on-screen keyboard's height so flex-end
        // pushes the sheet above the keyboard instead of behind it. On
        // desktop / when no keyboard is visible, keyboardOffset is 0.
        padding: useBottomSheet
          ? `0 0 ${keyboardOffset}px 0`
          : `1rem 1rem calc(1rem + ${keyboardOffset}px) 1rem`,
        transition: 'padding-bottom 0.18s ease-out',
        animation: 'emFadeIn 0.25s ease-out',
        overflowX: 'hidden',
      }}
    >
      {/* Backdrop with blur */}
      <div
        onClick={closeOnBackdrop ? handleClose : undefined}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          zIndex: 0
        }}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'em-modal-title' : undefined}
        data-testid={dataTestId}
        style={{
          position: 'relative',
          zIndex: 1050,
          backgroundColor: 'var(--color-surface, #FFFFFF)',
          borderRadius: useBottomSheet ? '1.25rem 1.25rem 0 0' : '0.875rem',
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.2)',
          maxWidth: useBottomSheet ? '100%' : maxWidth,
          width: '100%',
          minWidth: 0,
          maxHeight: useBottomSheet ? '95vh' : '90vh',
          ...(useBottomSheet && mobileFullHeight ? { height: '95dvh' } : {}),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          direction: isRTL ? 'rtl' : 'ltr',
          animation: useBottomSheet
            ? 'emSlideInBottom 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)'
            : 'emSlideUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent strip + optional progress bar */}
        <div style={{ flexShrink: 0, position: 'relative' }}>
          <div style={{
            height: '3px',
            background: 'linear-gradient(90deg, var(--color-primary, #355760) 0%, var(--color-primary-light, #4A7A87) 100%)',
            borderRadius: useBottomSheet ? '0' : '0.875rem 0.875rem 0 0',
          }} />
          {hasProgress && (
            <div style={{
              position: 'absolute',
              top: 0,
              insetInlineStart: 0,
              height: '3px',
              width: `${Math.min(100, Math.max(0, progress))}%`,
              backgroundColor: 'var(--color-success, #16A34A)',
              borderRadius: useBottomSheet ? '0' : '0.875rem 0 0 0',
              transition: 'width 0.4s ease',
              zIndex: 1,
            }} />
          )}
        </div>

        {/* Mobile drag handle */}
        {useBottomSheet && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '0.625rem 0 0.25rem',
            flexShrink: 0,
          }}>
            <div style={{
              width: '40px',
              height: '4px',
              borderRadius: '9999px',
              backgroundColor: 'var(--color-border-dark, #CDD3DE)',
            }} />
          </div>
        )}

        {/* Sticky Header */}
        {hasHeader && (
          <div style={{
            flexShrink: 0,
            minWidth: 0,
            padding: isMobile ? '1rem 1.25rem' : '1.25rem 1.5rem',
            borderBottom: '1px solid var(--color-border, #E2E6EE)',
            backgroundColor: 'var(--color-surface, #FFFFFF)',
            boxShadow: headerShadow ? '0 2px 8px rgba(0, 0, 0, 0.04)' : 'none',
            transition: 'box-shadow 0.2s ease',
            zIndex: 2
          }}>
            {headerContent || (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2
                    id="em-modal-title"
                    style={{
                      fontSize: isMobile ? '1.125rem' : '1.25rem',
                      fontWeight: '700',
                      margin: 0,
                      color: 'var(--color-text-primary, #1A1F36)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {titleIconNode}
                    {title}
                  </h2>
                  {subtitle && (
                    <p style={{
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-tertiary, #697386)',
                      margin: '0.375rem 0 0 0',
                      fontWeight: '400'
                    }}>
                      {subtitle}
                    </p>
                  )}
                </div>
                {!hideCloseButton && (
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    aria-label={t('common.close', 'Close')}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      padding: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0.5rem',
                      transition: 'background-color 0.15s',
                      opacity: isSubmitting ? 0.5 : 1,
                      flexShrink: 0,
                      marginInlineStart: '0.75rem',
                      // A 44x44 tap target (WCAG 2.5.5) on every device.
                      minWidth: 44,
                      minHeight: 44,
                    }}
                    onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = 'var(--color-surface-alt, #F1F4F8)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <X size={20} color="var(--color-text-tertiary, #697386)" />
                  </button>
                )}
              </div>
            )}

            {/* Step Indicator */}
            {steps && steps.length > 0 && (
              <StepIndicator steps={steps} isRTL={isRTL} />
            )}
          </div>
        )}

        {/* Scrollable Body */}
        <div
          ref={bodyRef}
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: bodyPadding
          }}
        >
          {children}
        </div>

        {/* Fixed Footer */}
        {hasFooter && (
          <div className="em-modal-footer" style={{
            flexShrink: 0,
            minWidth: 0,
            padding: isMobile ? '0.875rem 1.25rem' : '1rem 1.5rem',
            // Clear the home indicator only. The 70px that used to be added
            // here to "clear the bottom nav" was dead space: the nav sits at
            // z-index 1000 and this modal renders above it at 1050, so the
            // sheet already covers it — the padding just pushed the footer
            // buttons up and stole a chunk of every mobile sheet.
            paddingBottom: isMobile ? 'calc(0.875rem + env(safe-area-inset-bottom, 0px))' : '1rem',
            borderTop: '1px solid var(--color-border, #E2E6EE)',
            backgroundColor: 'var(--color-surface-alt, #F7F9FC)',
            boxShadow: footerShadow ? '0 -2px 8px rgba(0, 0, 0, 0.04)' : 'none',
            transition: 'box-shadow 0.2s ease',
            zIndex: 2
          }}>
            {footer}
          </div>
        )}
      </div>

      {/* Shared Animations */}
      <style>{`
        @media (max-width: 768px) {
          .em-modal-footer .btn,
          .em-modal-footer button[type="submit"] {
            width: auto;
            min-width: 0;
            padding-inline: 14px;
            white-space: normal;
          }
        }
        @keyframes emFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes emSlideUp {
          from { transform: translateY(16px) scale(0.98); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes emSlideInBottom {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
}
